import * as turf from "@turf/turf";
import {GeometryUtil} from "../geometry";
import {FeatureUtil} from "../feature";
import {Feature, LineString} from "geojson";

export class ShapeGenerator {

  /**
   * Generates a square from two consecutive points
   * @param {Number[]} pointA
   * @param {Number[]} pointB
   * @param {"left"|"right"} orientation
   */
  static generateSquareFromConsecutivePoints(pointA, pointB, orientation = "left") {
    let sideLength = GeometryUtil.pointsDistance(pointA, pointB);
    let bearing = GeometryUtil.pointsBearing(pointA, pointB);

    switch (orientation) {
      case "left":
        bearing = bearing - 90;
        break;
      case "right":
        bearing = bearing + 90;
        break;
    }

    let pointC = GeometryUtil.pointsTranslate(pointB, sideLength, bearing);
    let pointD = GeometryUtil.pointsTranslate(pointA, sideLength, bearing);

    return [pointA, pointB, pointC, pointD];
  }

  /**
   * Cut polygon by line
   * @param polygonFeature
   * @param lineFeature
   * @returns {FeatureCollection<Geometry, Properties>}
   * @link https://github.com/woodland-solutions-group/turf-cut pretty much copy/paste
   */
  static cutPolygon(polygonFeature, lineFeature) {
    // validation
    if (polygonFeature.geometry === void 0 || polygonFeature.geometry.type !== "Polygon") {
      throw ("\"turf-cut\" only accepts Polygon type as victim input");
    }
    if (lineFeature.geometry === void 0 || lineFeature.geometry.type !== "LineString") {
      throw ("\"turf-cut\" only accepts LineString type as axe input");
    }
    // updated from inside to booleanPointInPolygon 8/22 JM
    if (turf.booleanPointInPolygon(turf.point(lineFeature.geometry.coordinates[0]), polygonFeature)
      || turf.booleanPointInPolygon(turf.point(lineFeature.geometry.coordinates
      [lineFeature.geometry.coordinates.length - 1]), polygonFeature)) {
      throw ("Both first and last points of the polyline must be outside of the polygon");
    }

    // erase replaced by difference and buffer function changed significantly
    let _axe = turf.buffer(lineFeature, 0.001, {units: "meters"}),
      _body = turf.difference(polygonFeature, _axe),
      pieces = [];

    if (_body.geometry.type == "Polygon") {
      pieces.push(turf.polygon(_body.geometry.coordinates));
    } else {
      _body.geometry.coordinates.forEach(a => pieces.push(turf.polygon(a)));
    }

    pieces.forEach(a => {
      a.properties = polygonFeature.properties;
      a.geometry.coordinates[0] = FeatureUtil.deduplicateCoordinates(a.geometry.type, a.geometry.coordinates[0], 5);
    });

    return turf.featureCollection(pieces);
  }

  /**
   * Cut line based on another line
   * @param originalLine
   * @param cutterLine
   * @returns {FeatureCollection<LineString>}
   */
  static cutLine(originalLine, cutterLine) {
    return turf.lineSplit(originalLine, cutterLine);
  }

  /**
   * Generates a square from two diagonal points
   * @param {Number[]} pointA
   * @param {Number[]} pointC
   * @returns {Number[][]}
   */
  static generateSquareFromDiagonalPoints(pointA, pointC) {
    let sideLength = GeometryUtil.pointsDistance(pointA, pointC) / Math.sqrt(2); /* length=diagonal/math.sqrt(s) */
    let bearing = GeometryUtil.pointsBearing(pointA, pointC);

    let pointB = GeometryUtil.pointsTranslate(pointA, sideLength, bearing + 45);
    let pointD = GeometryUtil.pointsTranslate(pointA, sideLength, bearing - 45);

    return [pointA, pointB, pointC, pointD];
  }

  /**
   * Generates a rectangle from three points
   * @param {Number[]} pointA
   * @param {Number[]} pointB
   * @param {Number[]} pointC
   * @returns {Number[][]}
   */
  static generateRectangleFromThreePoints(pointA, pointB, pointC) {
    let bearing = GeometryUtil.pointsBearing(pointA, pointB);

    let pointCDistanceToLine = this.triangleHeight(pointA, pointB, pointC);

    if (this.getPointOrientationToLine(pointC, pointA, pointB) < 0) {
      bearing -= 90;
    } else {
      bearing += 90;
    }

    pointC = GeometryUtil.pointsTranslate(pointB, pointCDistanceToLine, bearing);
    let pointD = GeometryUtil.pointsTranslate(pointA, pointCDistanceToLine, bearing);

    return [pointA, pointB, pointC, pointD];
  }

  /**
   * Generates a standard 2x1 rectangle along two points
   * @param {Number[]} pointA
   * @param {Number[]} pointB
   * @param {"left"|"right"} orientation
   */
  static generateSimpleRectangleFromTwoPoints(pointA, pointB, orientation = "left") {
    let sideLength = GeometryUtil.pointsDistance(pointA, pointB);
    let bearing = GeometryUtil.pointsBearing(pointA, pointB);

    switch (orientation) {
      case "left":
        bearing = bearing - 90;
        break;
      case "right":
        bearing = bearing + 90;
        break;
    }

    let pointC = GeometryUtil.pointsTranslate(pointB, sideLength / 2, bearing);
    let pointD = GeometryUtil.pointsTranslate(pointA, sideLength / 2, bearing);

    return [pointA, pointB, pointC, pointD];
  }

  /**
   * Generate a grid of rectangles inside a rectangle made by 3 points
   * @param {Number[]} pointA
   * @param {Number[]} pointB
   * @param {Number[]} pointC
   * @param {number} width
   * @param {number} height
   * @returns {(*|Number[])[]}
   */
  static generateRectangleGridFromThreePoints(pointA, pointB, pointC, width, height) {
    let initialBearing = GeometryUtil.pointsBearing(pointA, pointB), enclosingBearing = initialBearing;

    if (this.getPointOrientationToLine(pointC, pointA, pointB) < 0) {
      enclosingBearing -= 90;
    } else {
      enclosingBearing += 90;
    }

    let enclosingWidth = GeometryUtil.pointsDistance(pointA, pointB);
    let enclosingHeight = this.triangleHeight(pointA, pointB, pointC);

    if (enclosingHeight < height) {
      enclosingHeight = height;
    }

    let columns = Math.floor(enclosingWidth / width);
    let rows = Math.floor(enclosingHeight / height);

    let multiRectangle = [];

    for (let columnIndex = 0; columnIndex < columns; columnIndex++) {
      for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
        let subPointA = GeometryUtil.pointsTranslate(pointA, columnIndex * width, initialBearing);
        subPointA = GeometryUtil.pointsTranslate(subPointA, rowIndex * height, enclosingBearing);

        let subPointB = GeometryUtil.pointsTranslate(subPointA, width, initialBearing);
        let subPointC = GeometryUtil.pointsTranslate(subPointB, height, enclosingBearing);
        let subPointD = GeometryUtil.pointsTranslate(subPointA, height, enclosingBearing);
        multiRectangle.push([subPointA, subPointB, subPointC, subPointD, subPointA]);
      }
    }

    return multiRectangle;
  }

  static getPositiveBearings(pointA, pointB, pointC) {
    let bearingA = GeometryUtil.pointsBearing(pointB, pointA);
    let bearingC = GeometryUtil.pointsBearing(pointB, pointC);

    let angle = bearingC - bearingA;
    if (angle >= 180 || angle < 0) {
      let cup = bearingA;
      bearingA = bearingC;
      bearingC = cup;
    }

    return {bearingA, bearingC};
  }

  static generateAngleMeasurement(pointA, pointB, pointC): Feature<LineString> {
    let distance = GeometryUtil.pointsDistance(pointB, pointC) * 0.2;

    let angle = GeometryUtil.pointsAngle(pointA, pointB, pointC);

    const isClockwise = turf.booleanClockwise([pointA, pointB, pointC,pointA]);

    let {bearingA, bearingC} = this.getPositiveBearings(isClockwise?pointA:pointC, pointB, isClockwise?pointC:pointA);
    let labelPlacement, label;
    if (this.getPointOrientationToLine(pointC, pointA, pointB) < 0) {
      labelPlacement = GeometryUtil.pointsTranslate(pointB, distance, GeometryUtil.pointsBearing(pointB, pointA) + (angle / 2));
    } else {
      labelPlacement = GeometryUtil.pointsTranslate(pointB, distance, GeometryUtil.pointsBearing(pointB, pointA) - (angle / 2));
    }
    label = Math.round(angle);

    return {
      type: "Feature",
      geometry: turf.lineArc(pointB, distance, bearingA, bearingC, {units: "meters"}).geometry,
      properties: {
        labelPosition: labelPlacement, label: label, angle: label
      }
    };
  }

  static generateParallelogramFromThreePoints(pointA, pointB, pointC) {
    let distance = GeometryUtil.pointsDistance(pointB, pointC);

    let angle = GeometryUtil.pointsAngle(pointA, pointB, pointC);

    let bearingA = GeometryUtil.pointsBearing(pointB, pointA);
    let pointCOrientation = this.getPointOrientationToLine(pointC, pointA, pointB);

    if (pointCOrientation < 0) {
      angle = bearingA + angle;
    } else {
      angle = bearingA - angle;
    }

    return [pointA, pointB, GeometryUtil.pointsTranslate(pointB, distance, angle), GeometryUtil.pointsTranslate(pointA, distance, angle), pointA];
  }

  static generateParallelogramMatrix(parallelogramPoints, referencePoint) {
    let width = GeometryUtil.pointsDistance(parallelogramPoints[0], parallelogramPoints[1]);
    let height = GeometryUtil.pointsDistance(parallelogramPoints[1], parallelogramPoints[2]);

    let heightPoint = turf.transformRotate({
      type: "Point", coordinates: parallelogramPoints[1]
    }, 90, {pivot: parallelogramPoints[0]}).coordinates;

    let enclosingWidth = this.triangleHeight(parallelogramPoints[0], heightPoint, referencePoint);
    let enclosingHeight = this.triangleHeight(parallelogramPoints[0], parallelogramPoints[1], referencePoint);
    let enclosingOrientationWidth = GeometryUtil.pointsBearing(parallelogramPoints[0], parallelogramPoints[1]);
    let enclosingOrientationHeight = GeometryUtil.pointsBearing(parallelogramPoints[0], parallelogramPoints[3]);

    let matrixWidth = Math.floor(enclosingWidth / width);
    let matrixHeight = Math.floor(enclosingHeight / height);

    matrixWidth = matrixWidth === 0 ? 1 : matrixWidth;
    matrixHeight = matrixHeight === 0 ? 1 : matrixHeight;

    let matrix = [];

    let pointA, pointB, pointC, pointD;

    for (let widthIndex = 0; widthIndex < matrixWidth; widthIndex++) {
      for (let heightIndex = 0; heightIndex < matrixHeight; heightIndex++) {
        pointA = GeometryUtil.pointsTranslate(parallelogramPoints[0], widthIndex * width, enclosingOrientationWidth);
        pointB = GeometryUtil.pointsTranslate(parallelogramPoints[1], widthIndex * width, enclosingOrientationWidth);
        pointC = GeometryUtil.pointsTranslate(parallelogramPoints[2], widthIndex * width, enclosingOrientationWidth);
        pointD = GeometryUtil.pointsTranslate(parallelogramPoints[3], widthIndex * width, enclosingOrientationWidth);

        pointA = GeometryUtil.pointsTranslate(pointA, heightIndex * height, enclosingOrientationHeight);
        pointB = GeometryUtil.pointsTranslate(pointB, heightIndex * height, enclosingOrientationHeight);
        pointC = GeometryUtil.pointsTranslate(pointC, heightIndex * height, enclosingOrientationHeight);
        pointD = GeometryUtil.pointsTranslate(pointD, heightIndex * height, enclosingOrientationHeight);

        matrix.push([pointA, pointB, pointC, pointD, pointA]);
      }
    }

    matrix.shift();
    return matrix;
  }

  static generateRectangleRowFromTwoPoints(pointA, pointB, width, height, orientation = "left") {
    let initialBearing = GeometryUtil.pointsBearing(pointA, pointB), enclosingBearing = initialBearing;

    if (orientation === "left") {
      enclosingBearing -= 90;
    } else {
      enclosingBearing += 90;
    }

    let enclosingWidth = GeometryUtil.pointsDistance(pointA, pointB);

    let columns = Math.floor(enclosingWidth / width);

    let multiRectangle = [];

    for (let columnIndex = 0; columnIndex < columns; columnIndex++) {
      let subPointA = GeometryUtil.pointsTranslate(pointA, columnIndex * width, initialBearing);

      let subPointB = GeometryUtil.pointsTranslate(subPointA, width, initialBearing);
      let subPointC = GeometryUtil.pointsTranslate(subPointB, height, enclosingBearing);
      let subPointD = GeometryUtil.pointsTranslate(subPointA, height, enclosingBearing);
      multiRectangle.push([subPointA, subPointB, subPointC, subPointD, subPointA]);
    }

    return multiRectangle;
  }

  /**
   * Check if target point is on the left/right side of vector (pointA,pointB)
   * @param target
   * @param pointA
   * @param pointB
   * @returns {number}
   */
  static getPointOrientationToLine(target, pointA, pointB) {
    // @ts-ignore
    return Math.sign(((target[0] - pointA[0]) * (pointB[1] - pointA[1])) - ((target[1] - pointA[1]) * (pointB[0] - pointA[0])));
    // return Math.sign(((pointB[0] - pointA[0]) * (target[1] - pointA[1])) - ((pointB[1] - pointA[1]) * (target[0] - pointA[0])));
  }

  /**
   * Projects pointC on the line of pointA&pointB
   * @param {Number[]} pointA
   * @param {Number[]} pointB
   * @param {Number[]} pointC
   *
   * @see https://math.stackexchange.com/questions/62633/orthogonal-projection-of-a-point-onto-a-line
   */
  static projectPointOnLine(pointA, pointB, pointC) {
    let gradient = (pointB[1] - pointA[1]) / (pointB[0] - pointA[0]);
    let c = pointA[1] - gradient * pointA[0];

    return [(pointC[0] + gradient * pointC[1] - gradient * c) / (1 + gradient * gradient), (gradient * pointC[0] + gradient * gradient * pointC[1] + c) / (1 + gradient * gradient)];
  }

  /**
   * Get length of perpendicular line from pointC onto (pointA, pointB) vector
   * @param {Number[]} pointA
   * @param {Number[]} pointB
   * @param {number[]} pointC
   * @returns {number}
   */
  static triangleHeight(pointA, pointB, pointC) {
    let area = turf.area({type: "Polygon", coordinates: [[pointA, pointB, pointC]]});
    let base = GeometryUtil.pointsDistance(pointA, pointB);

    return 2 * (area / base);
  }
}
